import { mapValues, pickBy } from "@/helpers/utility/CollectionFunctions";
import { ensureInt, notNull } from "@/helpers/utility/Type";

import SimulationRound from "@editor/items/simulationRound/SimulationRound";
import Impulse from "@editor/items/impulse/Impulse";
import Incident from "@editor/items/incident/Incident";
import Action from "@editor/items/action/Action";
import Condition from "@editor/items/condition/Condition";

import type { FlowItem } from "../flow/FlowItem";
import type { IndexItem } from "../index/IndexItem";
import type { SimulationModelState } from "../model/ModelState";
import type { ImpulseItem } from "../impulse/ImpulseItem";
import type { TargetItem } from "../target/TargetItem";
import type { FlowBranchItem } from "../flow/FlowBranchItem";
import type { ScenarioItem } from "../scenario/ScenarioItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { RelationItem } from "../relation/RelationItem";
import type { ActionPlan } from "../simulationRound/ActionPlan";
import type { EndReason, SimulationItem } from "./SimulationItem";
import type { ScenarioEndItem } from "../scenarioEnd/ScenarioEndItem";
import type { RoundUnit } from "../simulationRound/SimulationRoundItem";
import type { MomentumItem } from "../momentum/MomentumItem";
import type { ExpressionValue } from "../expression/expressionFunctions";
import type { ExpressionParseContext } from "../effect/Expression";

import { calculateExpressionValues } from "../index/indexFunctions";
import { evaluateRelation } from "../relation/Relation";
import { calculateFlowBranches } from "../flow/flowBranchFunctions";
import { splitUid, toUid, type Uid } from "@/helpers/utility/Id";
import { getVirtualStartRound } from "./simulationFunctions";

interface SimulationModelData {
  scenario?: ScenarioItem | null;
  rounds: SimulationRound[];
  actions: Action[];
  incidents: Incident[];
  scenarioEnds: ScenarioEnd[];
  relations: {
    // by end item
    target: { [targetId: number]: RelationItem[] };
    resource: { [resourceId: number]: RelationItem[] };
  };
  flows: {
    [flowId: number]: FlowItem & { branches: FlowBranchItem[] };
  };
  momentums: { [uid: string]: MomentumItem };
  randomSeed: number;
  netItems: {
    target: { [targetId: number]: TargetItem };
    resource: { [resourceId: number]: ResourceItem };
    index: { [indexId: number]: IndexItem };
  };
}

export class Simulation implements SimulationItem, SimulationModelData {
  id: number;
  uid: `simulation_${number}`;
  type: "simulation";
  createdAt: number;
  docs: Record<string, string>;
  order: number;

  title: string;
  description: string;
  complete: boolean;
  maxRounds: number;
  scenarioId: number | null;

  startElementValues: { [elementUid: string]: ExpressionValue };
  startRandomValues: { [randomValueUid: string]: number };

  endReason: EndReason | null;

  currentRoundIndex: number; // null while the simulation did not start

  // modeldata
  scenario?: ScenarioItem;
  relations: {
    // by end item
    target: { [targetId: number]: RelationItem[] };
    resource: { [resourceId: number]: RelationItem[] };
  };
  flows: {
    [flowId: number]: FlowItem & { branches: FlowBranchItem[] };
  };

  targetIds: number[];
  resourceIds: number[];
  indexIds: number[];
  actionIds: number[];
  incidentIds: number[];

  useMomentums: boolean;
  useDelays: boolean;
  useScheduledEffects: boolean;

  actions: Action[];
  incidents: Incident[];

  netItems: {
    target: { [targetId: number]: TargetItem };
    resource: { [resourceId: number]: ResourceItem };
    index: { [indexId: number]: IndexItem };
  };

  momentums: { [uid: Uid<'target'|'resource'>]: MomentumItem };
  scenarioEnds: ScenarioEnd[];

  rounds: SimulationRound[];
  actionPlans: { [roundIndex: string]: ActionPlan };

  roundUnit: RoundUnit;
  roundLength: number;
  durationType: "roundAmount" | "timeframe";
  timeStart: string | null;

  _eventListeners: { [eventType: string]: ((payload: unknown) => void)[] };
  randomSeed: number;

  constructor(
    _simulationData: Partial<SimulationItem>,
    modelData: Partial<SimulationModelData> = {}
  ) {
    const simulationData = clone(_simulationData);
    this.currentRoundIndex = simulationData.currentRoundIndex ?? -1;

    this.id = simulationData?.id ?? -1;
    this.uid = simulationData.uid ?? `simulation_${this.id}`;
    this.type = "simulation";
    this.createdAt = simulationData.createdAt ?? Date.now();
    this.docs = simulationData.docs ?? {};
    this.order = simulationData.order ?? -1;

    this.maxRounds = simulationData.maxRounds ?? 10;
    this.title = simulationData?.title ?? "";
    this.description = simulationData?.description ?? "";

    this.scenarioId = simulationData.scenarioId ?? null;
    this.complete = simulationData?.complete ?? false;

    this.endReason = simulationData.endReason ?? null;

    this.roundUnit = simulationData.roundUnit ?? "round";
    this.roundLength = simulationData.roundLength ?? 1;
    this.durationType = simulationData.durationType ?? "roundAmount";
    this.timeStart = simulationData.timeStart ?? null;

    this.netItems = modelData.netItems
      ? { ...modelData.netItems }
      : { target: {}, resource: {}, index: {} };

    this.targetIds = simulationData.targetIds?.slice() ?? [];
    this.resourceIds = simulationData.resourceIds?.slice() ?? [];
    this.indexIds = simulationData.indexIds?.slice() ?? [];

    this.startElementValues = simulationData?.startElementValues ?? {};
    this.startRandomValues = simulationData?.startRandomValues ?? {};

    this.actionIds = simulationData.actionIds ?? [];
    this.incidentIds = simulationData.incidentIds ?? [];

    this.useMomentums = simulationData.useMomentums ?? true;
    this.useDelays = simulationData.useDelays ?? true;
    this.useScheduledEffects = simulationData.useScheduledEffects ?? true;

    this.actionPlans = simulationData.actionPlans ?? {};

    this.rounds = (simulationData.rounds || []).map(
      (round) => new SimulationRound(round)
    );

    if (this.rounds.length === 0) {
      // pre-calculate all the start values of the indices
      const startIndexValues = calculateExpressionValues(
        modelData.netItems?.index || {},
        {
          ...this.getCommonParseContext(),
          roundIndex: 0,
          rounds: [
            {
              elementValues: this.startElementValues
            }
          ],
          randomValues: this.startRandomValues
        }
      );
      for (const indexUid in startIndexValues) {
        this.startElementValues[indexUid] = startIndexValues[indexUid];
      }
    }

    // Filter out all flows that are not connected to any element with a value.
    const existingElementUids = new Set(
      Object.keys(this.startElementValues)
    );
    const filteredFlows = pickBy(modelData.flows || {}, (flow) => {
      const branches = flow.branches;
      for (const branch of branches) {
        const elementUid = `${branch.elementType}_${branch.elementId}`;
        if (existingElementUids.has(elementUid)) {
          return true;
        }
      }
      return false;
    });

    if (this.rounds.length === 0) {
      // pre-calculate all flow start values
      const startFlowValues = calculateExpressionValues(filteredFlows, {
        ...this.getCommonParseContext(),
        roundIndex: 0,
        rounds: [{ elementValues: this.startElementValues }],
        randomValues: this.startRandomValues
      });
      for (const flowUid in startFlowValues) {
        this.startElementValues[flowUid] = startFlowValues[flowUid];
      }

      // pre-calculate all flow branch values
      for (const flow of Object.values(filteredFlows)) {
        const calculatedFlow = this.startElementValues[flow.uid];
        const flowValue = calculatedFlow.value;

        for (const branch of flow.branches) {
          const branchValue = evaluateRelation(branch, Number(flowValue));
          const minValue = calculatedFlow.minValue == null ? null : evaluateRelation(branch, calculatedFlow.minValue);
          const maxValue = calculatedFlow.maxValue == null ? null : evaluateRelation(branch, calculatedFlow.maxValue);
          const elementUid = `${branch.elementType}_${branch.elementId}`;

          this.startElementValues[branch.uid] = {
            value: branchValue,
            minValue: minValue,
            maxValue: maxValue,
            unit: this.startElementValues[elementUid]?.unit ?? "",
            unitValid: this.startElementValues[elementUid]?.unitValid ?? true,
            valid: true
          };
        }
      }
    }

    // MODELDATA

    this.scenario = modelData.scenario ?? undefined;
    this.scenarioEnds = modelData.scenarioEnds ?? [];
    this.relations = modelData.relations || { target: {}, resource: {} };
    this.flows = filteredFlows;
    /**
     * All the actions that the simulation has access to.
     * Usually restricted by the scenario.
     */
    this.actions = modelData.actions ?? [];
    this.incidents = modelData.incidents ?? [];

    this.momentums = modelData.momentums ?? {};

    this._eventListeners = {};
    this.randomSeed =
      modelData.randomSeed ?? Math.floor(Math.random() * 100_000);

    console.log("created simulation", clone(this));
  }

  /**
   * Simulate the next round of the simulation.
   * Ends the simulation if the last round is reached.
   */
  simulateRound() {
    if (this.complete) {
      throw new Error("simulation is complete");
    }

    const roundActionPlan = this.actionPlans?.[this.currentRound.index];

    if (!this.currentRound.played && this.currentRound.index >= 0) {
      this.playActions();
      this.currentRound.addEffects(roundActionPlan?.scheduledEffects ?? [], {
        ...this.getCommonParseContext(),
        roundIndex: this.currentRound.index,
        rounds: this.rounds
      });
      this.applyImpulses();
    }

    this.startNextRound();

    if (this.complete) {
      console.info("Simulation ended.");
      return;
    }

    // Simulate everything that happens in a round before any user interaction

    this.currentRound.addImpulsesFromPendingEffects({
      ...this.getCommonParseContext(),
      rounds: this.rounds
    });

    // pass on impulses
    this.createRelationImpulses("target");
    this.createRelationImpulses("resource");

    this.activateMomentums();

    this.playIncidents();

    // At this point, the round is half played through.
    // Wait for input.
  }

  /**
   * Run a simulation using ONLY the relations.
   * (no actions, incidents, momentums, ...)
   */
  simulateRelations(roundAmount: number) {
    for (let i = 0; i < roundAmount; i++) {
      this.startNextRound();
      const parseContext = {
        ...this.getCommonParseContext(),
        rounds: this.rounds
      };
      this.currentRound.addImpulsesFromPendingEffects(parseContext);
      const roundActionPlan = this.actionPlans?.[this.currentRound.index];
      this.currentRound.addEffects(
        roundActionPlan?.scheduledEffects ?? [],
        parseContext
      );
      this.createRelationImpulses("target");
      this.createRelationImpulses("resource");
      this.applyImpulses();
    }
  }

  /**
   * Setup the next round, increase the round counter and check if the simulation ended.
   * @returns success?
   */
  startNextRound() {
    if (this.currentRoundIndex >= 0) {
      const currentRound = this.currentRound;
      currentRound.played = true;
    }
    this.currentRoundIndex++;

    this.endIfMaxRoundsReached();
    if (this.complete) return false;

    // create a new round if there's none.
    if (this.currentRound == null) {
      this.rounds[this.currentRoundIndex] = new SimulationRound({
        index: this.currentRoundIndex
      });
    }

    // set the start values of the new round:
    const startState = this.getRoundStartState(this.currentRoundIndex);

    this.currentRound.startElementValues = clone(startState.elementValues);
    this.currentRound.elementValues = clone(startState.elementValues);

    this.currentRound.startRandomValues = clone(startState.randomValues);
    this.currentRound.randomValues = {};

    // TODO: remove index values from startElementValues and elementValues!!

    this.currentRound.incidentCounts = clone(startState.incidentCounts);
    this.currentRound.actionCounts = clone(startState.actionCounts);
    this.currentRound.triggerCounts = clone(startState.triggerCounts);
    this.currentRound.pendingEffects = startState.pendingEffects.map((effect) =>
      effect.clone()
    );

    // recalculate index values to account for round index changes
    // TODO: stabilize random values??
    const recalcParseContext = {
      ...this.getCommonParseContext(),
      rounds: this.rounds.map((round) => ({ ...round })),
      roundIndex: this.currentRoundIndex,
      randomValues: this.currentRound.startRandomValues
    };
    const startIndexValues = calculateExpressionValues(
      this.netItems.index || {},
      recalcParseContext
    );
    for (const indexUid in startIndexValues) {
      this.currentRound.startElementValues[indexUid] = {
        ...startIndexValues[indexUid]
      };
      this.currentRound.elementValues[indexUid] = {
        ...startIndexValues[indexUid]
      };
    }

    // recalculate flow values to account for round index changes
    const flowValues = this.getCalculatedFlows(recalcParseContext);
    for (const uid in flowValues) {
      this.currentRound.startElementValues[uid] = {
        ...flowValues[uid as Uid<'flowBranch'|'flow'>]
      };
      this.currentRound.elementValues[uid] = {
        ...flowValues[uid as Uid<'flowBranch'|'flow'>]
      };
    }

    // check: ABORT the simulation?
    const gameEnd = this.getApplyingEnd();
    if (gameEnd) {
      if (this.currentRoundIndex > 0) this.currentRoundIndex -= 1;
      console.debug("simulation end occured", gameEnd);
      this.endReason = {
        type: "aborted",
        endId: gameEnd.id,
        roundIndex: this.currentRoundIndex
      };
      this.complete = true;

      return false;
    }

    return true;
  }

  endIfMaxRoundsReached() {
    if (this.currentRoundIndex < 0) return false;

    if (this.maxRounds != null && this.currentRoundIndex >= this.maxRounds) {
      this.currentRoundIndex--;
      this.endReason = {
        type: "ended",
        roundIndex: this.currentRoundIndex
      };
      this.complete = true;
    }
  }

  /**
   * Play all actions that are applied to the current round:
   */
  playActions() {
    if (!this.currentRound) return;

    const roundIndex = this.currentRound.index;

    // make sure the played actions are in the order
    // they were added to the round!
    const actionIds = this.actionPlans[roundIndex].plannedActionIds;
    const actions = actionIds.map((actionId) => {
      return this.actions.find((action) => action.id == actionId);
    }).filter(notNull);

    const parseContext = {
      ...this.getCommonParseContext(),
      rounds: this.rounds,
      roundIndex: this.currentRoundIndex
    }

    // Calculate the costs of all actions at once to avoid cross-influences
    // through value changes.
    const actionCosts = actions.map((action) => action?.calculateCosts(parseContext));

    for (let i = 0; i < actions.length; i++) {
      const costs = actionCosts[i];
      const action = actions[i];
      if (!action) {
        throw new Error("no action");
      }
      if (costs == null) {
        throw new Error("no costs");
      }

      this.currentRound.playAction(action, this.rounds, parseContext, costs);
    }
  }

  applyImpulses() {
    if (!this.currentRound) return;
    this.currentRound.applyImpulses(this.netItems);

    this.calculateIndices({ randomValues: this.currentRound.startRandomValues });
    this.calculateFlows({ randomValues: this.currentRound.startRandomValues });
    this.activateFlows();

    // New end of round values
    this.calculateIndices({ randomValues: this.currentRound.randomValues });
    this.calculateFlows({ randomValues: this.currentRound.randomValues });
  }

  /**
   * Add an impulse to the current round
   */
  addImpulse(
    impulseData: ImpulseItem,
    affectedType: "target" | "resource",
    affectedId: number
  ) {
    if (!this.currentRound) {
      throw new Error("no current round");
    }
    this.currentRound.addImpulse(impulseData, affectedType, affectedId);
  }

  getOrCreateRoundActionPlan(roundIndex: number) {
    if (!this.actionPlans[roundIndex]) {
      this.actionPlans[roundIndex] = Simulation.createRoundActionPlan();
    }
    return this.actionPlans[roundIndex];
  }
  static createRoundActionPlan(): ActionPlan {
    return {
      plannedActionIds: [],
      scheduledEffects: [],
      allowedRelationIds: null,
      docs: {}
    };
  }

  /**
   *
   */
  createRelationImpulses(endItemType: "target" | "resource") {
    if (this.currentRound.played) return;

    // get all relations
    const relationsByItem = this.relations[endItemType];

    const allowedRelationIds =
      this.actionPlans?.[this.currentRound.index]?.allowedRelationIds;

    // loop through every item
    for (const itemId in relationsByItem) {
      let relations = relationsByItem[itemId];

      if (allowedRelationIds) {
        relations = relations.filter((relation) =>
          allowedRelationIds.includes(relation.id)
        );
      }
      if (relations.length == 0) continue;

      // get all impulses of all relations of this item
      const relationImpulses = relations.map((relation) =>
        Impulse.createFromRelation(
          {
            ...relation,
            delay: this.useDelays ? relation.delay : 0
          },
          this.rounds,
          this.currentRound.index
        )
      );

      const filteredImpulses = relationImpulses.filter(
        (impulse) => impulse != null
      );
      if (filteredImpulses.length === 0) continue;

      const totalImpulse = filteredImpulses.reduce(Impulse.add, undefined);

      if (totalImpulse) {
        // add the impulse to the list
        this.addImpulse(totalImpulse, endItemType, ensureInt(itemId));
      }
    }
  }

  /**
   * Create impulses of all target momenti.
   */
  activateMomentums() {
    if (!this.useMomentums) return;

    for (const _elementUid in this.momentums) {
      const elementUid = _elementUid as Uid<'target'|'resource'>;
      const momentum = this.momentums[elementUid];
      const { type, id } = splitUid(elementUid);

      const impulse = this.getMomentumImpulse(momentum);

      if (impulse) {
        this.addImpulse(impulse, type, id);
      }
    }
  }

  /**
   * Create an impulse based on the data of a momentum.
   */
  getMomentumImpulse(momentum: MomentumItem) {
    if (momentum.interval > 0) {
      // if the momentum has an interval,
      // only activate rounds in that interval.
      if (this.currentRound.index % momentum.interval != 0) {
        return undefined;
      }
    }

    // make sure the target for the momentum exists:
    if (momentum.elementType == 'target' && !this.targetIds.includes(momentum.elementId)) {
      console.warn(
        `momentum for deleted target ${momentum.elementId} still exists.`
      );
      return undefined;
    } else if (momentum.elementType == 'resource' && !this.resourceIds.includes(momentum.elementId)) {
      console.warn(
        `momentum for deleted resource ${momentum.elementId} still exists.`
      );
      return undefined;
    }

    const currentValue =
      this.currentRound.startElementValues[
        `${momentum.elementType}_${momentum.elementId}`
      ];

    const pointsToAdd = evaluateRelation(
      momentum,
      Number(currentValue.value || 0)
    );

    // don't create impulse if points are 0.
    if (pointsToAdd === 0) return undefined;

    return new Impulse({
      pointsToAdd: pointsToAdd,
      sourceValues: [
        {
          type: "momentum",
          id: momentum.id,
          value: pointsToAdd,
          effectType: "net"
        }
      ]
    });
  }

  calculateIndices(contextSettings: ExpressionParseContext) {
    const parseContext = {
      ...this.getCommonParseContext(),
      rounds: this.rounds,
      roundIndex: this.currentRound.index,
      randomValues: contextSettings.randomValues ?? this.currentRound.randomValues
    };

    const indexValues = calculateExpressionValues(
      this.netItems.index,
      parseContext
    );

    for (const indexUid in indexValues) {
      this.currentRound.elementValues[indexUid] = {
        ...indexValues[indexUid]
      };
    }
  }

  getCalculatedFlows(baseParseContext: ExpressionParseContext) {
    if (Object.values(this.flows).length === 0) return {};
    
    const parseContext = {
      ...baseParseContext
    };
    const roundIndex = parseContext.roundIndex ?? 0;

    const rounds = (parseContext.rounds ?? []).slice();

    const i = rounds.findIndex((round) => round.index == roundIndex);
    if (i == -1) return {};

    // clone the round to avoid changing the original
    const round = { index: roundIndex, elementValues: rounds[i].elementValues, startElementValues: rounds[i].startElementValues };
    rounds[i] = round;
    
    parseContext.rounds = rounds;
    
    const elementValues = round.elementValues ?? {};

    const activeFlows: Record<string, FlowItem & { branches: FlowBranchItem[] }> = {};
    const inactiveFlows: Record<string, FlowItem & { branches: FlowBranchItem[] }> = {};

    for (const flow of Object.values(this.flows)) {
      if (flow.interval) {
        if ((roundIndex + 1) % flow.interval != 0) {
          inactiveFlows[flow.id] = flow;
          continue;
        }
      }
      activeFlows[flow.id] = flow;
    }

    const values: Record<Uid<"flowBranch" | "flow">, ExpressionValue> = {};

    const flowValues = calculateExpressionValues(activeFlows, parseContext);
    for (const flowUid in flowValues) {
      elementValues[flowUid] = {
        ...flowValues[flowUid]
      };
      values[flowUid as Uid<'flow'>] = {
        ...flowValues[flowUid]
      };
    }

    for (const key in inactiveFlows) {
      const flow = inactiveFlows[key];
      const flowUid = flow.uid;
      const value: ExpressionValue = {
        value: 0,
        valid: true,
        minValue: 0,
        maxValue: 0,
        unit: "",
        unitValid: true
      };
      elementValues[flowUid] = value;
      values[flowUid] = value;
    }

    const branchesByFlow: { [flowId: number]: FlowBranchItem[] } = {};
    for (const key in this.flows) {
      const flow = this.flows[key];
      branchesByFlow[flow.id] = flow.branches;
    }

    
    const calculatedBranches = calculateFlowBranches(
      this.flows,
      branchesByFlow,
      elementValues
    );

    for (const [_, branch] of calculatedBranches) {
      const branchValue = branch.value;

      if (!elementValues[branch.uid]) {
        elementValues[branch.uid] = {
          value: branchValue,
          minValue: branchValue,
          maxValue: branchValue,
          unit: "",
          unitValid: true,
          valid: true
        };
      }
      elementValues[branch.uid].value = branchValue;

      values[branch.uid] = {
        value: branchValue,
        minValue: branchValue,
        maxValue: branchValue,
        unit: "",
        unitValid: true,
        valid: true
      };
    }

    return values;
  }

  calculateFlows(contextSettings: ExpressionParseContext = {}) {
    if (Object.values(this.flows).length === 0) return;
    if (this.currentRound.played) return;

    const values = this.getCalculatedFlows({
      ...this.getCommonParseContext(),
      rounds: this.rounds,
      roundIndex: this.currentRound.index,
      randomValues: contextSettings.randomValues ?? this.currentRound.randomValues
    });

    for (const uid in values) {
      const value = values[uid as Uid<'flowBranch'|'flow'>];
      this.currentRound.elementValues[uid] = {
        ...value
      };
    }
  }

  activateFlows() {
    if (Object.values(this.flows).length === 0) return;
    if (this.currentRound.played) return;

    const branches = Object.values(this.flows).flatMap((flow) => flow.branches);

    // collect impulses for all branches
    const impulses = new Map<Uid<'target'|'resource'>, Impulse>();
    for (const branch of branches) {
      const calculatedBranch = this.currentRound.elementValues[branch.uid];
      const branchValue = calculatedBranch.value;
      
      const elementUid = toUid(branch.elementType, branch.elementId);
      
      const impulse = new Impulse({
        pointsToAdd: Number(branchValue),
        sourceValues: [
          {
            type: "flowBranch",
            id: branch.id,
            value: Number(branchValue),
            effectType: "net"
          }
        ]
      });
      const elementImpulse = impulses.get(elementUid);
      if (elementImpulse) {
        impulses.set(elementUid, Impulse.add(elementImpulse, impulse));
      } else {
        impulses.set(elementUid, impulse);
      }
    }

    for (const [elementUid, impulse] of impulses) {
      const { type, id } = splitUid(elementUid);;
      this.currentRound.applyImpulse(impulse, type, id, this.netItems[type][id]);
      this.addImpulse(impulse, type, id);
    }
  }

  playIncidents() {
    // incidents that have not occurred more than allowed can be tested.
    const incidents = this.activatableIncidents;
    incidents.forEach((incident) => this.playIncident(incident));
  }

  playIncident(incident: Incident) {
    const currentRound = this.currentRound;
    if (this.currentRound.played) {
      throw new Error(`round ${this.currentRound.index} was already played`);
    }

    const activatedIncidentTrigger = incident.getApplyingTrigger(this.rounds);
    if (activatedIncidentTrigger == null) return false;

    // console.log("activate incident", incident);
    currentRound.addTriggeredIncident(activatedIncidentTrigger);

    // add incident effects to the round:
    this.currentRound.addEffects(incident.effects, {
      ...this.getCommonParseContext(),
      rounds: this.rounds
    });

    // console.debug("incident %cactivated!", "color: green;", incident);

    return true;
  }

  /**
   * Return the start state of a specific round.
   * The start state corresponds to the end state of the previous round.
   * @param roundIndex - the index of the round
   */
  getRoundStartState(roundIndex: number) {
    if (roundIndex <= 0) return this.virtualGameStartRound;

    const roundBeforeLast = this.rounds[roundIndex - 1];
    return { ...roundBeforeLast, index: roundIndex - 1 };
  }

  getApplyingEnd() {
    if (this.currentRound.index == -1) return undefined;

    // if the simulation is based on a scenario, it might have additional end conditions
    if (this.scenario == null) return undefined;
    if (this.scenarioEnds == null) return undefined;

    const roundsToTest = this.rounds.slice(0, -1);

    // check all ends, get the first that applies.
    const end = this.scenarioEnds.find(
      (end) =>
        end.conditions.length > 0 &&
        end.conditions.every((condition) => {
          const result = condition.evaluate(roundsToTest);
          return result;
        })
    );

    if (end != null) {
      return end;
    }

    return undefined;
  }

  updateActionPlan(roundIndex: number, actionPlan: ActionPlan) {
    if (actionPlan == null) {
      actionPlan = Simulation.createRoundActionPlan();
    }
    this.actionPlans[roundIndex] = actionPlan;
  }

  setMaxRounds(maxRounds: number) {
    this.maxRounds = maxRounds;

    while (this.rounds.length > maxRounds) {
      this.rounds.pop();

      if (this.currentRoundIndex >= this.rounds.length) {
        this.currentRoundIndex = this.rounds.length - 1;

        this.endIfMaxRoundsReached();
      }
    }

    if (this.rounds.length < maxRounds) {
      // Un-End the simulation if it ended because there were no rounds left
      if (this.endReason?.type === "ended") {
        this.endReason = null;
        this.complete = false;
      }
    }
  }

  getCommonParseContext() {
    return {
      targets: this.netItems.target,
      resources: this.netItems.resource,
      indices: this.netItems.index,
      simulationSettings: {
        roundUnit: this.roundUnit,
        roundLength: this.roundLength,
        durationType: this.durationType,
        timeStart: this.timeStart
      }
    };
  }

  clone() {
    return new Simulation(this);
  }

  // =======
  // getters

  get impulses() {
    return this.rounds.reduce(
      (
        roundList: {
          [roundIndex: number]: {
            target: { [targetId: number]: Impulse };
            resource: { [resourceId: number]: Impulse };
          };
        },
        round
      ) => {
        roundList[round.index] = round.impulses ?? {};
        return roundList;
      },
      {}
    );
  }

  /**
   * return a virtual round that represents
   * the start round of the whole simulation
   */
  get virtualGameStartRound() {
    return getVirtualStartRound(this);
  }

  get currentRound() {
    if (this.currentRoundIndex < 0) return this.virtualGameStartRound;
    return this.rounds[this.currentRoundIndex];
  }

  /**
   * get all incidents that have not exausted their maxOccurrence
   */
  get activatableIncidents() {
    if (this.incidents.length === 0) return [];

    const currentRound = this.currentRound;

    return this.incidents.filter((incident) => {
      if (incident.maxOccurrences == null) return true;
      if (currentRound.incidentCounts[incident.id] == null) return true;
      return currentRound.incidentCounts[incident.id] < incident.maxOccurrences;
    });
  }

  // EVENT LISTENING
  on(eventType: string, callback: (payload: unknown) => void) {
    if (this._eventListeners[eventType] == null) {
      this._eventListeners[eventType] = [];
    }
    this._eventListeners[eventType].push(callback);
  }
  off(eventType: string, callback: (payload: unknown) => void) {
    const listeners = this._eventListeners[eventType];
    const index = listeners.indexOf(callback);
    if (index < 0) return;
    listeners.splice(index, 1);
  }
  emit(eventType: string, payload = null) {
    if (this._eventListeners[eventType] == null) return;
    for (const callback of this._eventListeners[eventType]) {
      callback(payload);
    }
  }

  /**
   *
   */
  static instanceFromStore(modelState: SimulationModelState, id: number) {
    const item = clone(modelState.simulations.items[id]);

    const data = Simulation.loadGameStoreData(modelState, id);

    return new Simulation(item, data);
  }

  static loadGameStoreData(_modelState: SimulationModelState, id?: number) {
    const modelState = cloneSimulationModelState(_modelState);

    const data: SimulationModelData = {
      rounds: [],
      actions: [],
      incidents: [],
      netItems: {
        target: {},
        resource: {},
        index: {}
      },
      scenarioEnds: [],
      relations: {
        target: {},
        resource: {}
      },
      flows: {},
      momentums: {},
      randomSeed: Math.floor(Math.random() * 100_000)
    };

    const targets = modelState.targets.items;
    const resources = modelState.resources.items;
    const indices = modelState.indices.items;

    if (id != null) {
      const simulation = modelState.simulations.items[id];

      if (simulation.scenarioId != null) {
        data.scenario = modelState.scenarios.items[simulation.scenarioId];
      } else {
        data.scenario = null;
      }

      const actionIds = modelState.actions.order.filter((i) =>
        simulation.actionIds.includes(i)
      );
      data.actions = actionIds.map((actionId) =>
        Action.instanceFromStore(modelState, actionId)
      );
      const incidentIds = modelState.incidents.order.filter((i) =>
        simulation.incidentIds.includes(i)
      );
      const loadedIncidents = incidentIds.map((incidentId) =>
        Incident.instanceFromStore(modelState, incidentId)
      );
      data.incidents = loadedIncidents.filter(notNull);

      // filter out all targets that are not in the simulation
      for (const targetId in targets) {
        if (!simulation.targetIds.includes(Number(targetId))) {
          delete targets[targetId];
        }
      }
      // filter out all resources that are not in the simulation
      for (const resourceId in resources) {
        if (!simulation.resourceIds.includes(Number(resourceId))) {
          delete resources[resourceId];
        }
      }
      // filter out all indices that are not in the simulation
      for (const indexId in indices) {
        if (!simulation.indexIds.includes(Number(indexId))) {
          delete indices[indexId];
        }
      }

      if (data.scenario) {
        const scenario = data.scenario;
        data.scenarioEnds = Object.values(modelState.scenarioEnds.items)
          .filter(
            (end): end is ScenarioEndItem => end.scenarioId === scenario.id
          )
          .map((end) => ({
            ...end,
            conditions: Object.values(modelState.conditions.items)
              .filter((c) => c.srcType == end.type && c.srcId == end.id)
              .map((conditionItem) => new Condition(conditionItem))
          }));
      }
    }

    data.netItems = {
      target: targets,
      resource: resources,
      index: indices
    };

    // always add this data:
    data.relations = {
      target: activeByEndItem(
        Object.values(modelState.relations.items),
        "target"
      ),
      resource: activeByEndItem(
        Object.values(modelState.relations.items),
        "resource"
      )
    };

    data.flows = mapValues(modelState.flows.items, (flow) => ({
      ...flow,
      valueExpression:
        flow.valueInputMode == "expression"
          ? flow.valueExpression
          : (flow.valueReferenceType ? `${flow.valueReferenceType}_${flow.valueReferenceId}`: ''),
      branches: Object.values(modelState.flowBranches.items).filter(
        (branch) => branch.flowId === flow.id
      )
    }));

    const activeMomentumEntries = Object.values(
      modelState.momentums.items
    ).filter((momentum) => {
      if (momentum.elementType != "target") return true;
      const target = modelState.targets.items[momentum.elementId];
      return target.momentumActive;
    });
    const momentums: Record<string, MomentumItem> = {};
    for (const momentum of activeMomentumEntries) {
      momentums[`${momentum.elementType}_${momentum.elementId}`] = momentum;
    }
    data.momentums = momentums;

    return data;
  }
}

function cloneSimulationModelState(modelState: SimulationModelState): SimulationModelState {
  return {
    conditions: clone(modelState.conditions),
    effects: clone(modelState.effects),
    momentums: clone(modelState.momentums),
    targets: clone(modelState.targets),
    flows: clone(modelState.flows),
    flowBranches: clone(modelState.flowBranches),
    incidents: clone(modelState.incidents),
    incidentTriggers: clone(modelState.incidentTriggers),
    indices: clone(modelState.indices),
    actions: clone(modelState.actions),
    relations: clone(modelState.relations),
    requirements: clone(modelState.requirements),
    resources: clone(modelState.resources),
    scenarioEnds: clone(modelState.scenarioEnds),
    scenarios: clone(modelState.scenarios),
    ways: clone(modelState.ways),

    // do not clone simulations. This is expensive and not needed for most operations.
    simulations: modelState.simulations
  };
}

function activeByEndItem(items: RelationItem[], type: "target" | "resource") {
  return items.reduce(
    (byEndItem: { [targetId: number]: RelationItem[] }, relation) => {
      if (relation.updateType === "none") return byEndItem; // no nones.
      if (relation.endItemType != type) return byEndItem; // only by the specified end type

      if (!byEndItem[relation.endItemId]) {
        byEndItem[relation.endItemId] = [];
      }

      byEndItem[relation.endItemId].push(relation);
      return byEndItem;
    },
    {}
  );
}

/**
 * Clone an object removing any previous references to the original
 * @param {Object} object
 */
function clone<T>(object: T): T {
  if (!object) return object;
  return JSON.parse(JSON.stringify(object));
}

interface ScenarioEnd extends ScenarioEndItem {
  conditions: Condition[];
}

export default Simulation;
